import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {
  FormatDateEnum,
  IAddLearnerToSessionPayload,
  ILearnerData,
  ILearnerPayload,
  ILiteLearnerItem,
  LearnersListItem,
  ListResponse,
  LogsItem,
} from '@/models';
import {checkImageBase64Link, formatDate, http} from '@/utils';

import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {handleApiCallInStore} from './utils';
import {persist} from 'zustand/middleware';

export interface ICreateOrUpdateLearnerParams {
  learnerId?: string;
  onSuccess: () => void;
  payload: ILearnerPayload;
}

export interface ILearnersStore {
  learnersList: LearnersListItem[];
  allLearnersLite: ILiteLearnerItem[];
  allLearnersLiteNbHits: number;
  learnerInfo: ILearnerData;
  nbHits: number;
  learnerLogs: LogsItem[];
  getAllLearners: (params) => void;
  getLearnerById: (id: string, onSuccess: (data: ILearnerData) => void) => void;
  createOrUpdateLearner: (data: ICreateOrUpdateLearnerParams) => void;
  getLearnerLogs: (learnerId: string) => void;
  getAllLearnersLite: (params) => void;
  addLearnerToSession: (
    expId: string,
    sessionId: string,
    data: IAddLearnerToSessionPayload,
    onSuccess: () => void
  ) => void;
}

export const useLearnersStore = create(
  persist<ILearnersStore>(
    set => ({
      allLearnersLite: [],
      learnersList: [],
      learnerLogs: [],
      nbHits: 0,
      allLearnersLiteNbHits: 0,
      learnerInfo: undefined,
      addLearnerToSession: async (expId, sessionId, data, onSuccess) => {
        handleApiCallInStore(LoadingKeys.AddLearnerToSession, async () => {
          await http.post<IAddLearnerToSessionPayload>(
            API_ENDPOINTS.learners.addLearnerToSession(expId, sessionId),
            data
          );
          onSuccess?.();
        });
      },
      getAllLearnersLite: async params => {
        handleApiCallInStore(LoadingKeys.GetAllLearnersLite, async () => {
          const finalParams = {...params, role: 'learner', limit: 25};
          const {data} = await http.get<ListResponse<ILiteLearnerItem>>(
            API_ENDPOINTS.learners.allLite,
            {params: finalParams} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(({allLearnersLite}) => ({
            allLearnersLite:
              finalParams?.page === 0
                ? items ?? []
                : [...(allLearnersLite ?? []), ...(items ?? [])],
            allLearnersLiteNbHits: nbHits,
          }));
        });
      },
      getAllLearners: async params => {
        handleApiCallInStore(LoadingKeys.GetAllLearners, async () => {
          const {data} = await http.get<ListResponse<LearnersListItem>>(
            API_ENDPOINTS.learners.all,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({learnersList: items ?? [], nbHits}));
        });
      },
      getLearnerById: async (learnerId, onSuccess) => {
        handleApiCallInStore(LoadingKeys.GetLearnerById, async () => {
          const {data} = await http.get<ILearnerData>(
            `${API_ENDPOINTS.learners.byId}/${learnerId}`
          );
          set(() => ({learnerInfo: data}));
          onSuccess(data);
        });
      },
      createOrUpdateLearner: ({learnerId, onSuccess, payload}) => {
        handleApiCallInStore(LoadingKeys.createOrUpdateLearner, async () => {
          const {dateOfBirth, image, croppedImage, countryCode, phone} =
            payload;
          const requestPayload = {
            ...payload,
            dateOfBirth: dateOfBirth
              ? formatDate(dateOfBirth, FormatDateEnum.YMD)
              : undefined,
            phone: `${countryCode}${phone}`,
            role: 'learner',
            redirect: 'learners',
            image: checkImageBase64Link(image) ? image : undefined,
            croppedImage: checkImageBase64Link(croppedImage)
              ? croppedImage
              : undefined,
          };
          const apiLink = `${API_ENDPOINTS.learners.byId}${
            learnerId ? `/${learnerId}` : ''
          }`;
          if (learnerId) {
            await http.put(apiLink, requestPayload);
          } else {
            await http.post(apiLink, requestPayload);
          }

          await onSuccess();
        });
      },
      getLearnerLogs: learnerId => {
        handleApiCallInStore(LoadingKeys.LearnerLogs, async () => {
          const {data} = await http.get<{ items: LogsItem[] }>(
            `${API_ENDPOINTS.logs}${API_ENDPOINTS.learners.logs}?id=${learnerId}`
          );
          set(() => ({learnerLogs: data?.items}));
        });
      },
    }),
    {
      name: 'learners-storage',

    }
  )
);
